import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { apiClient, API_BASE } from '../api/client';
import {
  ErrorMessage,
  Card,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  Table,
  DataTable,
  Button,
  Select,
  Pagination,
  formatDate,
  formatEuro,
} from '../components';

// Types
interface UnmatchedTransaction {
  id: string;
  date: string;
  amount: number;
  description: string | null;
  counterparty: string | null;
  source: string;
  receiptStatus: 'missing' | 'waived' | 'pending' | 'matched';
}

interface AIMatchSuggestion {
  id: string;
  transactionId: string;
  receiptId: string;
  confidenceScore: number;
  matchedFields: {
    amount: number;
    date: number;
    description: number;
  };
  status: 'pending' | 'accepted' | 'rejected' | 'auto_accepted';
  createdAt: string;
  transaction?: {
    date: string;
    amount: number;
    description: string | null;
    counterparty: string | null;
  };
  receipt?: {
    filename: string;
    createdAt: string;
  };
}

interface UploadedFile {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  ocrStatus: 'pending' | 'processing' | 'completed' | 'failed';
}

const SOURCE_LABELS: Record<string, string> = {
  holvi: 'Holvi',
  op: 'OP',
  nordea: 'Nordea',
  sumup: 'SumUp',
  manual: 'Manuaalinen',
};

const STATUS_COLORS: Record<string, string> = {
  missing: 'bg-red-100 text-red-800',
  waived: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-gray-100 text-gray-800',
  matched: 'bg-green-100 text-green-800',
};

const STATUS_LABELS: Record<string, string> = {
  missing: 'Puuttuu',
  waived: 'Vapautettu',
  pending: 'Odottaa',
  matched: 'OK',
};

// Confidence Badge Component
function ConfidenceBadge({ score }: { score: number }) {
  let colorClass = '';
  let label = '';
  
  if (score >= 95) {
    colorClass = 'bg-green-100 text-green-800 border-green-200';
    label = 'Erinomainen';
  } else if (score >= 70) {
    colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-200';
    label = 'Hyvä';
  } else {
    colorClass = 'bg-red-100 text-red-800 border-red-200';
    label = 'Heikko';
  }
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${colorClass}`} title={`Luottamus: ${score}% - ${label}`}>
      <span className="font-bold">{score}%</span>
      <span className="text-xs">{label}</span>
    </div>
  );
}

// File Upload Drop Zone Component
function FileUploadDropZone({ 
  onUpload, 
  uploading 
}: { 
  onUpload: (files: FileList) => void;
  uploading: boolean;
}) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };

  return (
    <div
      onClick={() => fileInputRef.current?.click()}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
        ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
        ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,application/pdf"
        multiple
        disabled={uploading}
      />
      <div className="text-4xl mb-3">📎</div>
      <p className="text-sm font-medium text-gray-700">
        {uploading ? t('common.loading') : t('receipts.dropOrClick')}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {t('receipts.supportedFormats')}
      </p>
    </div>
  );
}

export default function ReceiptMatchingPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'unmatched' | 'suggestions' | 'manual'>('unmatched');
  
  const [unmatchedTransactions, setUnmatchedTransactions] = useState<UnmatchedTransaction[]>([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(true);
  const [unmatchedPage, setUnmatchedPage] = useState(1);
  const [unmatchedTotal, setUnmatchedTotal] = useState(0);
  const [unmatchedFilters, setUnmatchedFilters] = useState({ source: '' });
  
  const [aiSuggestions, setAiSuggestions] = useState<AIMatchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  
  const [uploading, setUploading] = useState(false);
  const [selectedTransactionForUpload, setSelectedTransactionForUpload] = useState<string | null>(null);
  
  const [availableFiles, setAvailableFiles] = useState<UploadedFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  
  const [manualMatchTransaction, setManualMatchTransaction] = useState<string | null>(null);
  const [manualMatchFile, setManualMatchFile] = useState<string | null>(null);
  
  const [error, setError] = useState('');
  
  const limit = 25;

  const fetchUnmatchedTransactions = useCallback(async () => {
    if (!activeOrg) return;
    
    setUnmatchedLoading(true);
    setError('');
    
    try {
      const offset = (unmatchedPage - 1) * limit;
      const params = new URLSearchParams();
      params.append('organization_id', activeOrg.id);
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      params.append('status', 'missing');
      if (unmatchedFilters.source) params.append('source', unmatchedFilters.source);
      
      const data = await apiClient.get<{
        transactions: UnmatchedTransaction[];
        pagination: { total: number };
      }>(`/missing-receipts?${params.toString()}`);
      
      setUnmatchedTransactions(data.transactions);
      setUnmatchedTotal(data.pagination.total);
    } catch {
      setError('Tapahtumien haku epäonnistui');
    } finally {
      setUnmatchedLoading(false);
    }
  }, [activeOrg, unmatchedPage, unmatchedFilters]);

  const fetchAiSuggestions = useCallback(async () => {
    if (!activeOrg) return;
    
    setSuggestionsLoading(true);
    try {
      const data = await apiClient.get<{ suggestions: AIMatchSuggestion[] }>(
        `/reconciliation/suggestions?organization_id=${activeOrg.id}&status=pending&limit=100`
      );
      setAiSuggestions(data.suggestions || []);
    } catch {
      console.error('Failed to fetch AI suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  }, [activeOrg]);

  const fetchAvailableFiles = useCallback(async () => {
    if (!activeOrg) return;
    
    setFilesLoading(true);
    try {
      const data = await apiClient.get<{ files: UploadedFile[] }>(`/files?organization_id=${activeOrg.id}&limit=100`);
      setAvailableFiles(data.files || []);
    } catch {
      console.error('Failed to fetch files');
    } finally {
      setFilesLoading(false);
    }
  }, [activeOrg]);

  const generateAiSuggestions = async () => {
    if (!activeOrg) return;
    
    setGeneratingSuggestions(true);
    setError('');
    
    try {
      const data = await apiClient.post<{
        suggestions_created: number;
        auto_accepted: number;
      }>('/reconciliation/ai-match', {
        organization_id: activeOrg.id,
        auto_accept_threshold: 95,
      });
      
      alert(`Luotu ${data.suggestions_created} ehdotusta, joista ${data.auto_accepted} hyväksytty automaattisesti.`);
      await fetchAiSuggestions();
    } catch {
      setError('Ehdotusten generointi epäonnistui');
      console.error('Failed to generate suggestions');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!activeOrg || files.length === 0) return;
    
    setUploading(true);
    setError('');
    
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('organization_id', activeOrg.id);
        
        const response = await fetch(`${API_BASE}/files`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Tiedoston ${file.name} lataus epäonnistui`);
        }
        
        const result = await response.json();
        
        if (selectedTransactionForUpload) {
          await createManualMatch(selectedTransactionForUpload, result.file.id);
        }
      }
      
      await fetchUnmatchedTransactions();
      await fetchAiSuggestions();
      await fetchAvailableFiles();
      
      setSelectedTransactionForUpload(null);
      alert('Tositteet ladattu onnistuneesti!');
    } catch (_err) {
      setError(_err instanceof Error ? _err.message : 'Lataus epäonnistui');
    } finally {
      setUploading(false);
    }
  };

  const createManualMatch = async (transactionId: string, fileId: string) => {
    if (!activeOrg) return;
    
    try {
      await apiClient.post(`/transactions/${transactionId}/link-receipt`, {
        organization_id: activeOrg.id,
        file_id: fileId,
      });
    } catch (_err) {
      console.error('Failed to link receipt:', _err);
      throw _err;
    }
  };

  const acceptSuggestion = async (suggestionId: string) => {
    if (!activeOrg) return;
    
    try {
      await apiClient.post(`/reconciliation/suggestions/${suggestionId}/accept?organization_id=${activeOrg.id}`, {});
      await fetchAiSuggestions();
      await fetchUnmatchedTransactions();
    } catch (_err) {
      setError('Hyväksyminen epäonnistui');
      console.error('Failed to accept:', _err);
    }
  };

  const rejectSuggestion = async (suggestionId: string) => {
    if (!activeOrg) return;
    
    try {
      await apiClient.post(`/reconciliation/suggestions/${suggestionId}/reject?organization_id=${activeOrg.id}`, {});
      await fetchAiSuggestions();
    } catch (_err) {
      setError('Hylkääminen epäonnistui');
      console.error('Failed to reject:', _err);
    }
  };

  const bulkAccept = async () => {
    for (const id of selectedSuggestions) {
      await acceptSuggestion(id);
    }
    setSelectedSuggestions(new Set());
  };

  const bulkReject = async () => {
    for (const id of selectedSuggestions) {
      await rejectSuggestion(id);
    }
    setSelectedSuggestions(new Set());
  };

  const handleManualMatch = async () => {
    if (!manualMatchTransaction || !manualMatchFile) return;
    
    try {
      await createManualMatch(manualMatchTransaction, manualMatchFile);
      await fetchUnmatchedTransactions();
      await fetchAvailableFiles();
      setManualMatchTransaction(null);
      setManualMatchFile(null);
      alert('Tosite liitetty onnistuneesti!');
    } catch {
      setError('Liittäminen epäonnistui');
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (activeOrg) {
      fetchUnmatchedTransactions();
      fetchAiSuggestions();
      fetchAvailableFiles();
    }
  }, [activeOrg, fetchUnmatchedTransactions, fetchAiSuggestions, fetchAvailableFiles]);

  useEffect(() => {
    if (activeTab === 'suggestions') {
      fetchAiSuggestions();
    } else if (activeTab === 'manual') {
      fetchAvailableFiles();
    }
  }, [activeTab, fetchAiSuggestions, fetchAvailableFiles]);

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Tositteiden täsmäytys" />
      
      {error && <ErrorMessage message={error} />}
      
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: 'unmatched', label: `📄 ${t('receiptMatching.missingReceipts')} (${unmatchedTotal})` },
          { key: 'suggestions', label: `🤖 ${t('receiptMatching.aiSuggestions')} (${aiSuggestions.length})` },
          { key: 'manual', label: '🔗 ' + t('receiptMatching.manualMatching') },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Card title="📎 Lataa uusi tosite">
        {selectedTransactionForUpload ? (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Lataa tosite tapahtumalle: <strong>
                {unmatchedTransactions.find(t => t.id === selectedTransactionForUpload)?.counterparty || 
                 unmatchedTransactions.find(t => t.id === selectedTransactionForUpload)?.description}
              </strong>
            </p>
            <button
              onClick={() => setSelectedTransactionForUpload(null)}
              className="text-xs text-blue-600 hover:text-blue-800 mt-1"
            >
              Peruuta valinta
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-600 mb-4">
            Lataa tosite valitsemalla tapahtuma alta tai pudota tiedosto suoraan tähän
          </p>
        )}
        <FileUploadDropZone onUpload={handleFileUpload} uploading={uploading} />
      </Card>

      {activeTab === 'unmatched' && (
        <Card title={`Tapahtumat ilman tositetta (${unmatchedTotal})`}>
          <div className="mb-4 flex gap-4">
            <Select
              label="Lähde"
              value={unmatchedFilters.source}
              onChange={(e) => setUnmatchedFilters(f => ({ ...f, source: e.target.value }))}
              options={[
                { value: '', label: 'Kaikki lähteet' },
                { value: 'holvi', label: 'Holvi' },
                { value: 'op', label: 'OP' },
                { value: 'nordea', label: 'Nordea' },
                { value: 'sumup', label: 'SumUp' },
                { value: 'manual', label: 'Manuaalinen' },
              ]}
            />
            <div className="flex items-end">
              <Button variant="secondary" onClick={fetchUnmatchedTransactions}>
                🔄 Päivitä
              </Button>
            </div>
          </div>

          <DataTable>
            <Table
              columns={[
                {
                  key: 'date',
                  header: 'Päivämäärä',
                  width: '120px',
                  render: (tx: UnmatchedTransaction) => formatDate(tx.date),
                },
                {
                  key: 'source',
                  header: 'Lähde',
                  width: '100px',
                  render: (tx: UnmatchedTransaction) => SOURCE_LABELS[tx.source] || tx.source,
                },
                {
                  key: 'counterparty',
                  header: 'Vastapuoli / Kuvaus',
                  render: (tx: UnmatchedTransaction) => (
                    <div>
                      <div className="font-medium">{tx.counterparty || '—'}</div>
                      <div className="text-sm text-gray-500">{tx.description}</div>
                    </div>
                  ),
                },
                {
                  key: 'amount',
                  header: 'Summa',
                  width: '120px',
                  align: 'right',
                  render: (tx: UnmatchedTransaction) => (
                    <span className={tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatEuro(tx.amount)}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Tila',
                  width: '120px',
                  render: (tx: UnmatchedTransaction) => (
                    <Badge className={STATUS_COLORS[tx.receiptStatus]}>
                      {STATUS_LABELS[tx.receiptStatus]}
                    </Badge>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  width: '200px',
                  align: 'right',
                  render: (tx: UnmatchedTransaction) => (
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setSelectedTransactionForUpload(tx.id)}
                      >
                        📎 Lisää tosite
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setManualMatchTransaction(tx.id);
                          setActiveTab('manual');
                        }}
                      >
                        🔗 Linkitä
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={unmatchedTransactions}
              keyExtractor={(tx) => tx.id}
              loading={unmatchedLoading}
              emptyMessage="Ei tapahtumia ilman tositetta"
            />
          </DataTable>

          {unmatchedTotal > limit && (
            <div className="mt-4">
              <Pagination
                currentPage={unmatchedPage}
                totalPages={Math.ceil(unmatchedTotal / limit)}
                onPageChange={setUnmatchedPage}
                totalItems={unmatchedTotal}
              />
            </div>
          )}
        </Card>
      )}

      {activeTab === 'suggestions' && (
        <Card title="🤖 AI-Täsmäytysehdotukset">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Tekoäly analysoi tapahtumat ja ehdottaa täsmäytyksiä luottamusarvion kera.
            </p>
            <Button
              variant="primary"
              onClick={generateAiSuggestions}
              disabled={generatingSuggestions}
            >
              {generatingSuggestions ? '⏳ Analysoidaan...' : '🤖 Generoi ehdotukset'}
            </Button>
          </div>

          {selectedSuggestions.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <span className="text-sm text-blue-800">{selectedSuggestions.size} ehdotusta valittu</span>
              <div className="flex gap-2">
                <Button variant="primary" size="sm" onClick={bulkAccept}>
                  ✓ Hyväksy valitut
                </Button>
                <Button variant="outline" size="sm" onClick={bulkReject}>
                  Hylkää valitut
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedSuggestions(new Set())}>
                  Tyhjennä
                </Button>
              </div>
            </div>
          )}

          {suggestionsLoading ? (
            <div className="text-center py-8 text-gray-500">⏳ Ladataan...</div>
          ) : aiSuggestions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-3">🤖</div>
              <p>Ei AI-ehdotuksia</p>
              <p className="text-sm">Paina "Generoi ehdotukset" analysoidaksesi tapahtumat</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <input
                  type="checkbox"
                  checked={selectedSuggestions.size === aiSuggestions.length && aiSuggestions.length > 0}
                  onChange={(e) => setSelectedSuggestions(e.target.checked ? new Set(aiSuggestions.map(s => s.id)) : new Set())}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-600">Valitse kaikki</span>
              </div>

              {aiSuggestions.map(suggestion => (
                <div 
                  key={suggestion.id} 
                  className={`p-4 border rounded-lg transition-colors ${
                    selectedSuggestions.has(suggestion.id) ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedSuggestions.has(suggestion.id)}
                      onChange={() => toggleSelection(suggestion.id)}
                      className="w-4 h-4 mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <ConfidenceBadge score={suggestion.confidenceScore} />
                        {suggestion.status === 'auto_accepted' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            Auto-hyväksytty
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Tapahtuma</p>
                          <p className="font-medium">
                            {suggestion.transaction?.counterparty || suggestion.transaction?.description}
                          </p>
                          <p className={`font-semibold ${
                            (suggestion.transaction?.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatEuro(suggestion.transaction?.amount || 0)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(suggestion.transaction?.date || '')}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Ehdotettu tosite</p>
                          <p className="font-medium">{suggestion.receipt?.filename}</p>
                          <p className="text-xs text-gray-500">
                            Ladattu: {formatDate(suggestion.receipt?.createdAt || '')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-700 mb-2">Täsmäytyksen perusteet:</p>
                        <div className="flex gap-4">
                          <span>Summa: {Math.round(suggestion.matchedFields.amount * 100)}%</span>
                          <span>Päivä: {Math.round(suggestion.matchedFields.date * 100)}%</span>
                          <span>Kuvaus: {Math.round(suggestion.matchedFields.description * 100)}%</span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => acceptSuggestion(suggestion.id)}
                        >
                          ✓ Hyväksy
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => rejectSuggestion(suggestion.id)}
                        >
                          Hylkää
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'manual' && (
        <Card title="🔗 Manuaalinen täsmäytys">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                1. Valitse tapahtuma
              </label>
              <Select
                value={manualMatchTransaction || ''}
                onChange={(e) => setManualMatchTransaction(e.target.value || null)}
                options={[
                  { value: '', label: 'Valitse tapahtuma...' },
                  ...unmatchedTransactions.map(tx => ({
                    value: tx.id,
                    label: `${formatDate(tx.date)} - ${tx.counterparty || tx.description || 'Tuntematon'} (${formatEuro(tx.amount)})`,
                  })),
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                2. Valitse tosite
              </label>
              <Select
                value={manualMatchFile || ''}
                onChange={(e) => setManualMatchFile(e.target.value || null)}
                options={[
                  { value: '', label: filesLoading ? t('common.loading') : t('receipts.selectReceipt') },
                  ...availableFiles.map(file => ({
                    value: file.id,
                    label: `${file.originalName} (${new Date(file.createdAt).toLocaleDateString('fi-FI')})`,
                  })),
                ]}
              />
            </div>

            <div className="flex gap-4">
              <Button
                variant="primary"
                onClick={handleManualMatch}
                disabled={!manualMatchTransaction || !manualMatchFile}
              >
                🔗 {t('receipts.attachReceipt')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setManualMatchTransaction(null);
                  setManualMatchFile(null);
                }}
              >
                {t('receipts.clear')}
              </Button>
            </div>
          </div>

          <div className="mt-8">
            <h4 className="text-sm font-medium text-gray-700 mb-3">{t('receipts.availableReceipts')}</h4>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {availableFiles.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">{t('receipts.noReceipts')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">{t('receipts.file')}</th>
                      <th className="px-4 py-2 text-left">{t('receipts.date')}</th>
                      <th className="px-4 py-2 text-left">OCR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {availableFiles.map(file => (
                      <tr 
                        key={file.id}
                        className={`cursor-pointer hover:bg-gray-50 ${manualMatchFile === file.id ? 'bg-blue-50' : ''}`}
                        onClick={() => setManualMatchFile(file.id)}
                      >
                        <td className="px-4 py-2">{file.originalName}</td>
                        <td className="px-4 py-2">{formatDate(file.createdAt)}</td>
                        <td className="px-4 py-2">
                          <Badge className={
                            file.ocrStatus === 'completed' ? 'bg-green-100 text-green-800' :
                            file.ocrStatus === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                            file.ocrStatus === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {file.ocrStatus === 'completed' ? t('receipts.ocrCompleted') :
                             file.ocrStatus === 'processing' ? t('receipts.ocrProcessing') :
                             file.ocrStatus === 'failed' ? t('receipts.ocrFailed') :
                             t('receipts.ocrPending')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
